import React, { useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  CardContent,
  Collapse,
  IconButton,
  Divider,
} from "@mui/material";
import { useFormik } from "formik";
import CloseIcon from "@mui/icons-material/Close";
import {
  useCreateEmailTemplatesMutation,
  useGetEmailTemplatesQuery,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { useStyles } from "../../../theme/appStyles";
import { toProperCase } from "../../../Components/Validation/toProperCase";
import {
  EmailTemplateOption,
  IEmailTemplet,
} from "../../../Api/Interface/api.interface";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { styled } from "@mui/material/styles";

const ExpandMore = styled((props: any) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }: any) => ({
  transform: !expand ? "rotate(0deg)" : "rotate(180deg)",
  transition: theme.transitions.create("transform", {
    duration: theme.transitions.duration.shortest,
  }),
}));

const Index = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [newDepartment, { isLoading }] = useCreateEmailTemplatesMutation();
  const { showErrorMessage, showMessage } = useNotifier();
  const [selectedpolicyProductId, setSelectedpolicyProductId] = useState<
    string | ""
  >("");
  const [selectedIC, setSelectedIC] = useState<string | "">("");
  const [selectedOption, setSelectedOption] =
    useState<EmailTemplateOption | null>(null);
  const [expandedTemplateId, setExpandedTemplateId] = useState<string | null>(
    null
  );

  const { data: userroleData, isLoading: userroleIsLoading } =
    useGetEmailTemplatesQuery({
      searchText: "",
    });

  const userroleList = useMemo(() => {
    return userroleData?.data as IEmailTemplet[];
  }, [userroleData?.data]);

  // const userRoleOptions = useMemo(() => {
  //   if (!Array.isArray(userroleList)) return [];
  //   return userroleList.map((cust) => ({
  //     label: `${toProperCase(cust.template_name)}`,
  //     value: cust.id,
  //     subject: cust.subject,
  //     message: cust.message,
  //   }));
  // }, [userroleList]);

  const userRoleOptions: EmailTemplateOption[] = useMemo(() => {
    if (!Array.isArray(userroleList)) return [];
    return userroleList.map((cust) => ({
      label: toProperCase(cust.template_name),
      value: cust.id,
      subject: cust.subject,
      message: cust.message,
    }));
  }, [userroleList]);

  const formik = useFormik({
    initialValues: {
      id: "",
      template_name: "",
      subject: "",
      message: "",
    },

    onSubmit: async (values, { resetForm }) => {
      try {
        const temData = {
          subject: values.subject,
          message: values.message,
          template_name: values.template_name,
        };

        const temDataId = {
          id: values.id,
          subject: values.subject,
          message: values.message,
          template_name: values.template_name,
        };

        const payload = selectedOption ? temDataId : temData;

        const addCompanyResponse = await newDepartment(payload).unwrap();
        if (!addCompanyResponse.status) {
          showErrorMessage(addCompanyResponse.message);
        } else {
          showMessage(addCompanyResponse.message);
          resetForm();
          setSelectedOption(null);
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const formValid = useMemo(() => {
    return (
      formik.values.template_name &&
      formik.values.subject &&
      formik.values.message
    );
  }, [formik]);

  const handleAutocompleteChange = (
    event: React.SyntheticEvent,
    newValue: EmailTemplateOption | null
  ) => {
    setSelectedOption(newValue);

    if (newValue) {
      formik.setValues({
        id: newValue.value,
        template_name: newValue.label,
        subject: newValue.subject || "",
        message: newValue.message || "",
      });
    } else {
      formik.resetForm();
    }
  };

  const toggleExpand = (id: string) => {
    setExpandedTemplateId((prev) => (prev === id ? null : id));
  };

  return (
    <div>
      <Grid container spacing={3}>
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <form onSubmit={formik.handleSubmit}>
            <Grid container>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Card className={classes.card}>
                  <Grid
                    container
                    justifyContent={"space-between"}
                    spacing={5}
                    sx={{ borderBottom: 2, padding: 2 }}
                    className={classes.titleGrid}
                  >
                    <Grid
                      item
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      pt={2}
                      alignContent={"center"}
                    >
                      <Typography className={classes.cardTitle}>
                        Email Template Setting
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid container spacing={3} p={"24px"}>
                    <Grid item lg={6} md={6} sm={12} xs={12}>
                      <Grid container alignItems="center">
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography variant="subtitle1">
                            Template Name{" "}
                            <span className={classes.requiedfield}>*</span>
                          </Typography>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <TextField
                            placeholder="Template Name"
                            size="small"
                            {...formik.getFieldProps("template_name")}
                            className={classes.textField}
                            InputProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                            InputLabelProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <Grid container alignItems="center">
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography variant="subtitle1">
                            Subject{" "}
                            <span className={classes.requiedfield}>*</span>
                          </Typography>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <TextField
                            placeholder="Subject"
                            size="small"
                            multiline
                            rows={3}
                            {...formik.getFieldProps("subject")}
                            className={classes.textField}
                            InputProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                            InputLabelProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item lg={12} md={12} sm={12} xs={12} pb={5}>
                      <Grid container alignItems="center">
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography variant="subtitle1">
                            Message{" "}
                            <span className={classes.requiedfield}>*</span>
                          </Typography>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          {/* <TextField
                            placeholder="message"
                            size="small"
                            multiline
                            rows={5}
                            {...formik.getFieldProps("message")}
                            className={classes.textField}
                            InputProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                            InputLabelProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                          /> */}
                          <ReactQuill
                            value={formik.values.message}
                            onChange={(value) =>
                              formik.setFieldValue("message", value)
                            }
                            placeholder="Type your message..."
                            theme="snow"
                            modules={{
                              toolbar: [
                                [{ font: [] }],
                                [{ header: [1, 2, 3, 4, 5, 6, false] }],
                                ["bold", "italic", "underline", "strike"],
                                [{ color: [] }, { background: [] }],
                                [{ script: "sub" }, { script: "super" }],
                                [{ list: "ordered" }, { list: "bullet" }],
                                [{ indent: "-1" }, { indent: "+1" }],
                                [{ direction: "rtl" }],
                                [{ align: [] }],
                                ["blockquote", "code-block"],
                                ["link", "image", "video"],
                                ["clean"],
                              ],
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item lg={12} md={12} sm={12} xs={12} pt={5}>
                      <Grid
                        container
                        spacing={2}
                        justifyContent={"space-between"}
                      >
                        <Grid
                          item
                          lg={4}
                          md={6}
                          sm={12}
                          xs={12}
                          sx={{ display: "flex", justifyContent: "flex-end" }}
                        >
                          <Autocomplete
                            options={userRoleOptions}
                            loading={userroleIsLoading}
                            // value={
                            //   userRoleOptions.find(
                            //     (option) =>
                            //       String(option.value) ===
                            //       selectedpolicyProductId
                            //   ) || null
                            // }
                            // getOptionLabel={(option: CustomerOptionString) =>
                            //   option.label
                            // }
                            // onChange={(
                            //   event,
                            //   newValue: CustomerOptionString | null
                            // ) => {
                            //   setSelectedIC(String(newValue?.value ?? ""));
                            // }}

                            value={selectedOption}
                            getOptionLabel={(option) => option.label}
                            onChange={handleAutocompleteChange}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                InputLabelProps={{ shrink: true }}
                                placeholder="Template Name  .."
                              />
                            )}
                            sx={{ flexGrow: 1, width: "100%" }}
                            size="small"
                            // className={classes.searchTextField}
                          />
                        </Grid>
                        <Grid
                          item
                          lg={8}
                          md={6}
                          sm={12}
                          xs={12}
                          sx={{ display: "flex", justifyContent: "flex-end" }}
                        >
                          <Button
                            variant="outlined"
                            startIcon={<CloseIcon />}
                            className={classes.addButton}
                            onClick={() => formik.resetForm()}
                          >
                            Cancel
                          </Button>
                          <Box m={0.5}></Box>

                          <Button
                            variant="contained"
                            startIcon={
                              isLoading ? (
                                <CircularProgress
                                  size={18}
                                  className={classes.isLoading}
                                />
                              ) : selectedOption ? (
                                <EditIcon />
                              ) : (
                                <AddIcon />
                              )
                            }
                            className={classes.addButton}
                            onClick={() => formik.handleSubmit()}
                            disabled={!formValid || isLoading}
                          >
                            {selectedOption ? "Edit" : "Create"}
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            </Grid>
          </form>
        </Grid>

        <Grid item lg={12} md={12} sm={12} xs={12}>
          <Card className={classes.card} sx={{ minHeight: 400 }}>
            <Grid
              container
              justifyContent={"space-between"}
              spacing={3}
              sx={{ borderBottom: 2, padding: 2 }}
              className={classes.titleGrid}
            >
              <Grid
                item
                lg={12}
                md={12}
                sm={12}
                xs={12}
                pt={2}
                alignContent={"center"}
              >
                <Typography className={classes.cardTitle}>Templates</Typography>
              </Grid>
            </Grid>
            <Grid container spacing={3} p={2}>
              {userroleList?.length ? (
                userroleList.map((template) => (
                  <Grid item xs={12} md={12} lg={12} key={template.id}>
                    <Card variant="outlined" className={classes.card}>
                      <CardContent
                        onClick={() => toggleExpand(template.id)}
                        sx={{ cursor: "pointer" }}
                      >
                        <Grid
                          container
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Grid item xs>
                            <Typography
                              variant="subtitle1"
                              fontWeight={600}
                              sx={{ cursor: "pointer" }}
                              onClick={() => toggleExpand(template.id)}
                            >
                              {template.template_name}
                            </Typography>
                          </Grid>
                          <Grid item>
                            <ExpandMore
                              expand={expandedTemplateId === template.id}
                              onClick={() => toggleExpand(template.id)}
                              aria-expanded={expandedTemplateId === template.id}
                            >
                              <ExpandMoreIcon />
                            </ExpandMore>
                          </Grid>
                        </Grid>

                        <Typography
                          variant="body2"
                          color="textSecondary"
                          mt={1}
                          noWrap
                        >
                          Subject: {template.subject}
                        </Typography>
                      </CardContent>

                      <Collapse
                        in={expandedTemplateId === template.id}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Divider />
                        <CardContent>
                          <Typography
                            component="div"
                            dangerouslySetInnerHTML={{
                              __html: template.message,
                            }}
                          />
                        </CardContent>
                      </Collapse>
                    </Card>
                  </Grid>
                ))
              ) : (
                <Grid item xs={12}>
                  <Typography textAlign="center">
                    No templates found.
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
};

export default Index;
